import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + "/app";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function AuthShell({ title, children }) {
  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 lg:block">
        <img src="https://images.unsplash.com/photo-1548690312-e3b507d8c110?w=1200&q=80" alt="" className="h-full w-full object-cover" />
      </div>
      <div className="flex w-full items-center justify-center px-5 py-12 lg:w-1/2">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-between">
            <Link to="/" className="font-display text-lg font-bold tracking-tight sm:text-2xl">Frenchies Fitness &amp; Pilates</Link>
            <ThemeToggle />
          </div>
          <h1 className="mt-8 font-display text-3xl font-bold tracking-tight">{title}</h1>
          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function Login() {
  const { t } = useI18n();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.role === "admin" ? "/admin" : u.role === "coach" ? "/coach" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.loginTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>{t("auth.email")}</Label>
          <Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-12" />
        </div>
        <div>
          <Label>{t("auth.password")}</Label>
          <Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-12" />
        </div>
        {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full">
          {loading ? t("common.loading") : t("nav.login")}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("auth.or")}<span className="h-px flex-1 bg-border" /></div>
      <Button variant="outline" data-testid="google-login-btn" onClick={googleLogin} className="w-full rounded-full">{t("auth.google")}</Button>
      <div className="mt-6 flex items-center justify-between text-sm">
        <Link to="/forgot" className="text-muted-foreground hover:text-primary">{t("auth.forgot")}</Link>
        <Link to="/register" data-testid="go-register" className="font-medium text-primary">{t("nav.signup")}</Link>
      </div>
    </AuthShell>
  );
}

export function Register() {
  const { t, lang, tl } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ first_name: "", last_name: "", email: "", password: "", phone: "", id_type: "cedula", id_number: "" });
  const [terms, setTerms] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!terms) { setError(t("auth.terms")); return; }
    if (!f.id_number) { setError(tl({ es: "El documento es obligatorio.", en: "Document is required.", fr: "Le document est obligatoire." })); return; }
    setLoading(true);
    try {
      await register({ ...f, language: lang, terms_accepted: terms, marketing_consent: marketing });
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={t("auth.registerTitle")}>
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>{t("auth.firstName")}</Label><Input data-testid="register-firstname" value={f.first_name} onChange={(e) => setF({ ...f, first_name: e.target.value })} required className="mt-1.5 h-12" /></div>
          <div><Label>{t("auth.lastName")}</Label><Input data-testid="register-lastname" value={f.last_name} onChange={(e) => setF({ ...f, last_name: e.target.value })} required className="mt-1.5 h-12" /></div>
        </div>
        <div><Label>{t("auth.email")}</Label><Input data-testid="register-email" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required className="mt-1.5 h-12" /></div>
        <div><Label>{t("auth.phone")}</Label><Input data-testid="register-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="mt-1.5 h-12" /></div>
        <div>
          <Label>{tl({ es: "Documento (facturación)", en: "Document (billing)", fr: "Document (facturation)" })}</Label>
          <div className="mt-1.5 grid grid-cols-3 gap-3">
            <select data-testid="register-id-type" value={f.id_type} onChange={(e) => setF({ ...f, id_type: e.target.value })}
              className="col-span-1 h-12 rounded-lg border border-border bg-card px-3 text-sm">
              <option value="cedula">Cédula</option>
              <option value="ruc">RUC</option>
              <option value="pasaporte">{tl({ es: "Pasaporte", en: "Passport", fr: "Passeport" })}</option>
            </select>
            <Input data-testid="register-id-number" required placeholder={tl({ es: "N° de documento", en: "Document no.", fr: "N° de document" })}
              value={f.id_number} onChange={(e) => setF({ ...f, id_number: e.target.value })} className="col-span-2 h-12" />
          </div>
        </div>
        <div><Label>{t("auth.password")}</Label><Input data-testid="register-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required className="mt-1.5 h-12" /></div>
        <label className="flex items-start gap-2 text-sm"><input data-testid="register-terms" type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" />{t("auth.terms")}</label>
        <label className="flex items-start gap-2 text-sm text-muted-foreground"><input data-testid="register-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-1" />{t("auth.marketing")}</label>
        {error && <p data-testid="register-error" className="text-sm text-destructive">{error}</p>}
        <Button type="submit" data-testid="register-submit" disabled={loading} className="w-full rounded-full">
          {loading ? t("common.loading") : t("nav.signup")}
        </Button>
      </form>
      <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground"><span className="h-px flex-1 bg-border" />{t("auth.or")}<span className="h-px flex-1 bg-border" /></div>
      <Button variant="outline" data-testid="google-register-btn" onClick={googleLogin} className="w-full rounded-full">{t("auth.google")}</Button>
      <p className="mt-6 text-sm">{t("auth.hasAccount")} <Link to="/login" data-testid="go-login" className="font-medium text-primary">{t("nav.login")}</Link></p>
    </AuthShell>
  );
}

export function Forgot() {
  const { t, tl } = useI18n();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const api = (await import("@/lib/api")).default;
    try {
      await api.post("/auth/forgot-password", { email });
    } catch {
      // Intentionally silent: never reveal whether the email exists (avoids enumeration).
    }
    setSent(true); setLoading(false);
  };
  return (
    <AuthShell title={t("auth.resetTitle")}>
      {sent ? (
        <div data-testid="forgot-sent" className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          {tl({
            es: "Si existe una cuenta con ese correo, te enviamos un enlace para restablecer tu contraseña. Revisa tu bandeja de entrada (y el spam).",
            en: "If an account exists for that email, we've sent you a link to reset your password. Check your inbox (and spam).",
            fr: "Si un compte existe pour cet e-mail, nous t'avons envoyé un lien pour réinitialiser ton mot de passe. Vérifie ta boîte de réception (et les spams).",
            ru: "Если аккаунт с этой почтой существует, мы отправили ссылку для сброса пароля. Проверь входящие (и спам).",
          })}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div><Label>{t("auth.email")}</Label><Input data-testid="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 h-12" /></div>
          <Button type="submit" data-testid="forgot-submit" disabled={loading} className="w-full rounded-full">{loading ? t("common.loading") : t("auth.sendReset")}</Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-medium text-primary">{t("common.back")}</Link></p>
    </AuthShell>
  );
}

export function ResetPassword() {
  const { t, tl } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError(tl({ es: "Mínimo 8 caracteres.", en: "Minimum 8 characters.", fr: "8 caractères minimum.", ru: "Минимум 8 символов." })); return; }
    if (password !== confirm) { setError(tl({ es: "Las contraseñas no coinciden.", en: "Passwords don't match.", fr: "Les mots de passe ne correspondent pas.", ru: "Пароли не совпадают." })); return; }
    setLoading(true);
    const api = (await import("@/lib/api")).default;
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title={tl({ es: "Nueva contraseña", en: "New password", fr: "Nouveau mot de passe", ru: "Новый пароль" })}>
      {!token ? (
        <p data-testid="reset-no-token" className="text-sm text-destructive">
          {tl({ es: "Enlace inválido o incompleto.", en: "Invalid or incomplete link.", fr: "Lien invalide ou incomplet.", ru: "Неверная или неполная ссылка." })}
        </p>
      ) : done ? (
        <div data-testid="reset-success" className="rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          {tl({ es: "¡Contraseña actualizada! Redirigiendo al inicio de sesión…", en: "Password updated! Redirecting to login…", fr: "Mot de passe mis à jour ! Redirection vers la connexion…", ru: "Пароль обновлён! Перенаправляем на вход…" })}
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>{tl({ es: "Nueva contraseña", en: "New password", fr: "Nouveau mot de passe", ru: "Новый пароль" })}</Label>
            <Input data-testid="reset-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1.5 h-12" />
          </div>
          <div>
            <Label>{tl({ es: "Confirmar contraseña", en: "Confirm password", fr: "Confirmer le mot de passe", ru: "Подтвердить пароль" })}</Label>
            <Input data-testid="reset-confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required className="mt-1.5 h-12" />
          </div>
          {error && <p data-testid="reset-error" className="text-sm text-destructive">{error}</p>}
          <Button type="submit" data-testid="reset-submit" disabled={loading} className="w-full rounded-full">
            {loading ? t("common.loading") : tl({ es: "Restablecer", en: "Reset password", fr: "Réinitialiser", ru: "Сбросить" })}
          </Button>
        </form>
      )}
      <p className="mt-6 text-sm"><Link to="/login" className="font-medium text-primary">{t("common.back")}</Link></p>
    </AuthShell>
  );
}
